import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../Components/Footer/Footer';
import MainNav from '../../Components/Navbar/NewNavbar';
import Loading1 from '../../Components/Loading/Loading1';
import { HiOutlineShieldCheck, HiOutlineMail, HiOutlinePhone, HiOutlineLocationMarker } from 'react-icons/hi';

const Privacy = () => {
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 800);
        return () => clearTimeout(timer);
    }, []);

    if (isLoading) {
        return <Loading1 />;
    }

    const sections = [
        {
            title: "1. Introduction",
            content: "Astra Clean (\"we\", \"our\", \"us\") is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website and use our services."
        },
        {
            title: "2. Information We Collect",
            content: "We may collect and process the following data about you:",
            list: [
                { label: "Personal Information", text: "Name, email address, phone number, and home address." },
                { label: "Payment Information", text: "Credit/debit card details and billing address." },
                { label: "Service Information", text: "Details of the services you book with us." },
                { label: "Usage Data", text: "Information about how you use our website and services." },
                { label: "Technical Data", text: "IP address, browser type, operating system, and other technical details." }
            ]
        },
        {
            title: "3. How We Use Your Information",
            content: "We use the information we collect to provide and manage our services, process bookings, communicate updates, and comply with legal requirements."
        }
    ];

    return (
        <div className="bg-white min-h-screen">
            <MainNav />
            
            {/* Simple Premium Hero */}
            <section className="pt-40 pb-20 bg-secondary relative overflow-hidden">
                <div className="container-custom relative z-10">
                    <div className="max-w-3xl">
                        <span className="text-primary font-black uppercase tracking-[0.3em] text-xs">Trust & Safety</span>
                        <h1 className="text-5xl md:text-6xl font-heading font-black text-foreground mt-4">
                            Privacy <span className="text-primary italic">Policy</span>
                        </h1>
                        <p className="text-muted-foreground text-lg mt-6 leading-relaxed">
                            Your privacy is our priority. This document outlines how we handle your data with the highest standards of security and transparency.
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-24">
                <div className="container-custom">
                    <div className="grid lg:grid-cols-12 gap-16">
                        {/* Table of Contents - Left Side */}
                        <div className="lg:col-span-4 lg:sticky lg:top-32 h-fit space-y-6 hidden lg:block">
                            <div className="p-8 rounded-[2rem] bg-secondary/50 border border-border">
                                <h4 className="font-heading font-black text-xs uppercase tracking-widest text-primary mb-6 flex items-center gap-2">
                                    <HiOutlineShieldCheck size={18} />
                                    Quick Overview
                                </h4>
                                <nav className="space-y-4">
                                    {["Introduction", "Information Collect", "Usage", "Sharing", "Security", "Your Rights"].map(item => (
                                        <div key={item} className="text-sm font-bold text-muted-foreground hover:text-primary cursor-pointer transition-colors block">
                                            {item}
                                        </div>
                                    ))}
                                </nav>
                            </div>
                        </div>

                        {/* Main Text Content */}
                        <div className="lg:col-span-8 space-y-16">
                            <div className="prose prose-lg max-w-none">
                                <article className="space-y-12">
                                    <section className="space-y-4">
                                        <h2 className="text-2xl font-black text-foreground">1. Introduction</h2>
                                        <p className="text-foreground/70 leading-relaxed font-normal">
                                            Astra Clean ("we", "our", "us") is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website and use our services.
                                        </p>
                                    </section>

                                    <section className="space-y-6">
                                        <h2 className="text-2xl font-black text-foreground">2. Information We Collect</h2>
                                        <p className="text-foreground/70">We may collect and process the following data about you:</p>
                                        <ul className="grid gap-4 list-none p-0">
                                            {[
                                                { label: "Personal Info", desc: "Name, email, and contact details." },
                                                { label: "Payment Info", desc: "Billing address and processed transaction data." },
                                                { label: "Service Data", desc: "Details of bookings and preferences." },
                                                { label: "Technical Data", desc: "IP address and browser diagnostics." }
                                            ].map((item, i) => (
                                                <li key={i} className="p-6 rounded-2xl bg-secondary/30 flex items-start gap-4">
                                                    <HiOutlineShieldCheck className="text-primary mt-1 shrink-0" size={20} />
                                                    <div>
                                                        <span className="block font-black text-xs uppercase tracking-widest text-foreground">{item.label}</span>
                                                        <span className="text-sm text-foreground/60">{item.desc}</span>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </section>

                                    <section className="space-y-4">
                                        <h2 className="text-2xl font-black text-foreground">3. How We Use Data</h2>
                                        <p className="text-foreground/70 leading-relaxed">
                                            We use collected information to provide seamless services, improve user experience, and ensure safety across our platform. Your data helps us tailor our premium offerings to your specific lifestyle needs.
                                        </p>
                                    </section>

                                    {/* Simplified footer of the content */}
                                    <div className="pt-12 border-t border-border mt-20">
                                        <h3 className="text-xl font-black mb-6">Still have questions?</h3>
                                        <div className="grid sm:grid-cols-2 gap-4">
                                            <div className="flex items-center gap-4 p-6 rounded-2xl bg-primary/5 border border-primary/10">
                                                <HiOutlineMail className="text-primary" size={24} />
                                                <Link to="mailto:lakshjain1218@outlook.com" className="text-sm font-bold truncate">lakshjain1218@outlook.com</Link>
                                            </div>
                                            <div className="flex items-center gap-4 p-6 rounded-2xl bg-primary/5 border border-primary/10">
                                                <HiOutlinePhone className="text-primary" size={24} />
                                                <Link to="tel:+916366267882" className="text-sm font-bold">+91 63662 67882</Link>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Privacy;